/**
 * Tenant-configurable date format tokens.
 *
 * The active token is set once at app boot by
 * `useGlobalSettingsCacheSync()` (see shared/api/global-settings.ts) via
 * `setActiveDateFormat()`. Every date rendered through
 * `formatDate()`/`formatDateTime()` in shared/ui/utils.ts consults
 * `getActiveDateFormat()` and delegates to `formatWithToken()` — so no
 * call site needs to know about the setting.
 *
 * Adding a new token: extend `DateFormatToken`, add an entry to
 * `DATE_FORMAT_TOKENS`, mirror it in the DATE_FORMATS list in
 * modules/core/pages/GlobalSettingsPage.tsx AND the Rule::in list in
 * api/app/Http/Controllers/GlobalSettingsController.php.
 */
export type DateFormatToken =
  | 'DD/MM/YYYY'
  | 'DD-MM-YYYY'
  | 'DD.MM.YYYY'
  | 'MM/DD/YYYY'
  | 'MM-DD-YYYY'
  | 'YYYY-MM-DD'
  | 'YYYY/MM/DD'
  | 'D MMM YYYY'
  | 'D MMM, YYYY'
  | 'MMM D, YYYY'
  | 'DD MMM YYYY'
  | 'MMMM D, YYYY';

export const DEFAULT_DATE_FORMAT: DateFormatToken = 'DD/MM/YYYY';

const pad2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * One formatter per supported token. Kept as plain string concatenation
 * to avoid the per-call cost of constructing `Intl.DateTimeFormat` for
 * every cell in a large table.
 */
export const DATE_FORMAT_TOKENS: Record<DateFormatToken, (d: Date) => string> = {
  'DD/MM/YYYY':    (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`,
  'DD-MM-YYYY':    (d) => `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`,
  'DD.MM.YYYY':    (d) => `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`,
  'MM/DD/YYYY':    (d) => `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}/${d.getFullYear()}`,
  'MM-DD-YYYY':    (d) => `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()}`,
  'YYYY-MM-DD':    (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
  'YYYY/MM/DD':    (d) => `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`,
  'D MMM YYYY':    (d) => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`,
  'D MMM, YYYY':   (d) => `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}, ${d.getFullYear()}`,
  'MMM D, YYYY':   (d) => `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`,
  'DD MMM YYYY':   (d) => `${pad2(d.getDate())} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`,
  'MMMM D, YYYY':  (d) => `${MONTHS_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`,
};

/**
 * Ordered list for building `<Select>` dropdowns — value is the token, a
 * caller can build the label however it wants. `sample` uses 31 Dec 2026
 * so every option renders with two-digit day AND month for consistency.
 */
export const DATE_FORMAT_OPTIONS: { value: DateFormatToken; sample: string }[] = (
  Object.keys(DATE_FORMAT_TOKENS) as DateFormatToken[]
).map((token) => ({
  value: token,
  sample: DATE_FORMAT_TOKENS[token](new Date(2026, 11, 31)),
}));

/* -------------------------------------------------------------------------- */
/*  Active-token module state                                                 */
/* -------------------------------------------------------------------------- */

let activeToken: DateFormatToken = DEFAULT_DATE_FORMAT;

export function getActiveDateFormat(): DateFormatToken {
  return activeToken;
}

/** Called at app boot by `useGlobalSettingsCacheSync()`. */
export function setActiveDateFormat(token: string | null | undefined): void {
  if (token && token in DATE_FORMAT_TOKENS) {
    activeToken = token as DateFormatToken;
  } else {
    activeToken = DEFAULT_DATE_FORMAT;
  }
}

/** Format a Date with an explicit token (bypasses the active setting). */
export function formatWithToken(date: Date, token: DateFormatToken): string {
  return (DATE_FORMAT_TOKENS[token] ?? DATE_FORMAT_TOKENS[DEFAULT_DATE_FORMAT])(date);
}
